import { useEffect, useRef } from 'react';
import { ConfigProvider, Divider, Flex, Modal, Typography } from 'antd';
import Icon from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { ReactComponent as SosAlertLightSvg } from '../../assets/icons/sos-alert-modal-light.svg';
import { ReactComponent as SosAlertDarkSvg } from '../../assets/icons/sos-alert-modal-dark.svg';
import { ReactComponent as CancelSvg } from '../../assets/icons/cancel.svg';
import { ReactComponent as CancelBlackSvg } from '../../assets/icons/cancel-black.svg';
import { DARK, HYPHEN } from '../../utilities/constants';
import './AlertPopup.css';
import PropTypes from 'prop-types';

const { Text } = Typography;

const AlertPopup = ({
  open,
  onClose,
  title,
  data,
  additionalFields = [],
  footerButtons,
}) => {
  const { theme } = useTheme();
  const autoCloseTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    autoCloseTimerRef.current = setTimeout(() => {
      onClose();
    }, 3000);

    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [open, onClose]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginTop: 0,
            fontSize: '1rem',
          },
          Modal: {
            titleFontSize: '1.5rem',
          },
          Divider: {
            colorSplit: '#656A6F',
          },
        },
      }}
    >
      <Modal
        open={open}
        centered
        title={
          <Flex vertical>
            <Flex justify="space-between" align="flex-start">
              <Flex
                gap={8}
                align="center"
                className="sos-alert-modal-header-title"
              >
                <Icon
                  component={
                    theme === DARK ? SosAlertDarkSvg : SosAlertLightSvg
                  }
                  className="sos-alert-modal-header-title-icon"
                />
                <Text strong>{title}</Text>
              </Flex>
              <Icon
                component={theme === DARK ? CancelSvg : CancelBlackSvg}
                onClick={onClose}
                className="sos-alert-modal-header-close-icon"
              />
            </Flex>
            <Divider />
          </Flex>
        }
        footer={footerButtons}
        className="sos-alert-modal"
        closable={false}
        width="40%"
      >
        <Flex vertical gap="1.5rem">
          <Flex vertical gap={12}>
            <Typography variant="body1">
              <strong>Trip ID: </strong>
              {data?.trip_id || HYPHEN}
              <strong> | </strong>
              <strong>Device ID: </strong>
              {data?.device_id || HYPHEN}
            </Typography>
            <ConfigProvider
              theme={{
                components: {
                  Divider: {
                    colorSplit: 'var(--tertiary-stroke-color, #383B3E)',
                  },
                },
              }}
            >
              <Divider />
            </ConfigProvider>
          </Flex>

          {additionalFields.map((field, index) => (
            <Typography key={index} variant="body1">
              <strong>{field.label}: </strong>
              {field.value || HYPHEN}
            </Typography>
          ))}

          <Typography variant="body1">
            <strong>Location: </strong>
            {data?.latitude}, {data?.longitude}
          </Typography>
        </Flex>
      </Modal>
    </ConfigProvider>
  );
};

AlertPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  data: PropTypes.shape({
    trip_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    device_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  additionalFields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
    })
  ),
  footerButtons: PropTypes.arrayOf(PropTypes.node),
};

export default AlertPopup;
